import { Editor, Element as SlateElement, Range, Transforms, Node } from 'slate';
import { ReactEditor } from 'slate-react';
import { htmlToSlate } from 'src/components/RichSlate/deserialize';
import { LIST_TYPES, SLATE_NODE_TYPES, SLATE_TEXT_FORMATS, TEXT_ALIGN_TYPES } from './const';
import { slateToText, slateToHtml } from './serialize';

export const applyPlugins = (editor, plugins) => {
  return plugins.reduce((e, p) => p(e), editor);
};

export const getNodeType = (format) => {
  switch (format) {
    case SLATE_TEXT_FORMATS.BOLD:
    case SLATE_TEXT_FORMATS.ITALIC:
    case SLATE_TEXT_FORMATS.UNDERLINE:
    case SLATE_TEXT_FORMATS.STRIKETHROUGH:
      return SLATE_NODE_TYPES.MARK;

    case SLATE_TEXT_FORMATS.HEADING_ONE:
    case SLATE_TEXT_FORMATS.HEADING_TWO:
    case SLATE_TEXT_FORMATS.HEADING_THREE:
    case SLATE_TEXT_FORMATS.ALIGN_RIGHT:
    case SLATE_TEXT_FORMATS.ALIGN_LEFT:
    case SLATE_TEXT_FORMATS.ALIGN_CENTER:
    case SLATE_TEXT_FORMATS.BULLETED_LIST:
    case SLATE_TEXT_FORMATS.NUMBERED_LIST:
    case SLATE_TEXT_FORMATS.LIST_ITEM:
    case SLATE_TEXT_FORMATS.PARAGRAPH:
    case SLATE_TEXT_FORMATS.BLOCK_QUOTE:
    case SLATE_TEXT_FORMATS.DD_VARIATION:
      return SLATE_NODE_TYPES.BLOCK;

    case SLATE_TEXT_FORMATS.LINK:
    case SLATE_TEXT_FORMATS.BANK_MESSAGE:
    case SLATE_TEXT_FORMATS.PROMPT_TAG:
      return SLATE_NODE_TYPES.INLINE;

    default:
      return null;
  }
};

export const HOVERING_TOOLBAR_POSITIONS = {
  bottom: 'bottom',
  top: 'top',
};

export const isNonFocusedEditorSelectionValid = (editor) => {
  const { selection } = editor;
  const selectionValid = !!(
    selection &&
    !Range.isCollapsed(selection) &&
    Editor.string(editor, selection)
  );
  return selectionValid;
};
export const isEditorSelectionValid = (editor) => {
  const { selection } = editor;
  const inFocus = ReactEditor.isFocused(editor);
  return !!(
    selection &&
    inFocus &&
    !Range.isCollapsed(selection) &&
    Editor.string(editor, selection)
  );
};

export const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
    })
  );

  return !!match;
};

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleBlock = (editor, format) => {
  const isAlignment = TEXT_ALIGN_TYPES.includes(format);
  const isList = LIST_TYPES.includes(format);
  const isSelectionValid = isEditorSelectionValid(editor);
  const isActive = isBlockActive(editor, format, isAlignment ? 'align' : 'type');

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties;
  if (isAlignment) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive
        ? SLATE_TEXT_FORMATS.PARAGRAPH
        : isList
        ? SLATE_TEXT_FORMATS.LIST_ITEM
        : format,
    };
  }
  Transforms.setNodes(editor, newProperties, {
    split: !isAlignment && isSelectionValid,
  });

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === SLATE_TEXT_FORMATS.LINK,
  });
  return !!link;
};

export const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === SLATE_TEXT_FORMATS.LINK,
  });
};

export const wrapLink = (editor, url) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: SLATE_TEXT_FORMATS.LINK,
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const insertBankMessage = (editor, bankMessage) => {
  const ancestorNode = removePromptAncestorTags(editor);

  let tagNode, tagNodePath;
  if (ancestorNode) {
    tagNode = ancestorNode.node;
    tagNodePath = ancestorNode.path;
  }
  const newNode = {
    type: SLATE_TEXT_FORMATS.BANK_MESSAGE,
    bankMessageId: bankMessage.id,
    children: [
      {
        type: SLATE_TEXT_FORMATS.BANK_MESSAGE,
        bankMessageId: bankMessage.id,
        text: bankMessage.name,
      },
    ],
    ...bankMessage,
  };
  if (tagNode && tagNodePath) {
    Transforms.removeNodes(editor, { at: tagNodePath });
  }
  Transforms.insertNodes(editor, newNode);
  //insert separation between nodes/text
  Transforms.insertFragment(editor, [{ type: SLATE_NODE_TYPES.INLINE, children: [{ text: ' ' }] }]);

  focusEditor({ editor });
};

export const insertProductFromUrl = (editor, title, article) => {
  const ancestorNode = removePromptAncestorTags(editor);
  let tagNode, tagNodePath;
  if (ancestorNode) {
    tagNode = ancestorNode.node;
    tagNodePath = ancestorNode.path;
  }
  const newNode = {
    type: SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
    title: title,
    article: article,
    children: [
      {
        type: SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
        title: title,
        article: article,
        text: title,
      },
    ],
  };
  if (tagNode && tagNodePath) {
    Transforms.removeNodes(editor, { at: tagNodePath });
  }
  Transforms.insertNodes(editor, newNode);
  //insert separation between nodes/text
  Transforms.insertFragment(editor, [{ type: SLATE_NODE_TYPES.INLINE, children: [{ text: ' ' }] }]);

  focusEditor({ editor });
};

export const insertResourceUrl = (editor, url) => {
  const ancestorNode = removePromptAncestorTags(editor);

  let tagNode, tagNodePath;
  if (ancestorNode) {
    tagNode = ancestorNode.node;
    tagNodePath = ancestorNode.path;
  }
  const newNode = {
    type: SLATE_TEXT_FORMATS.RESOURCE_URL,
    resourceUrl: url,
    children: [
      {
        type: SLATE_TEXT_FORMATS.RESOURCE_URL,
        text: url,
      },
    ],
  };

  if (tagNode && tagNodePath) {
    Transforms.removeNodes(editor, { at: tagNodePath });
  }
  Transforms.insertNodes(editor, newNode);
  //insert separation between nodes/text
  Transforms.insertFragment(editor, [{ type: SLATE_NODE_TYPES.INLINE, children: [{ text: ' ' }] }]);

  focusEditor({ editor });
};

const removePromptAncestorTags = (editor) => {
  // If ancestor is a prompt tag, bank message, or product description it should be removed.
  let ancestorNode;

  for (const nodeFormat of [
    SLATE_TEXT_FORMATS.PROMPT_TAG,
    SLATE_TEXT_FORMATS.BANK_MESSAGE,
    SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
    SLATE_TEXT_FORMATS.RESOURCE_URL,
  ]) {
    ancestorNode = findAncestorByType(editor, nodeFormat);
    if (ancestorNode?.node !== null) {
      break;
    }
  }

  return ancestorNode;
};

export const insertLink = (editor, url, options) => {
  if (editor.selection) {
    wrapLink(editor, url);

    if (options?.withSpacePostfix) {
      Transforms.insertFragment(editor, [
        { type: SLATE_NODE_TYPES.INLINE, children: [{ text: ' ' }] },
      ]);
    }
  }
};

export const focusEditor = ({ editor, atEnd = false, ignoreSelection = false }) => {
  if (ignoreSelection || !editor?.selection) {
    if (atEnd) {
      Transforms.select(editor, Editor.end(editor, []));
    } else {
      Transforms.select(editor, Editor.start(editor, []));
    }
  }
  ReactEditor.focus(editor);
};

export const insertNodesToEditorEnd = (editor, nodes) => {
  Transforms.insertNodes(editor, nodes, {
    at: [editor.children.length - 1],
  });
};

export const insertNodesToEditorAtSelection = (editor, nodes) => {
  const selectedNodes = getSelectionNodes(editor);

  if (selectedNodes?.[0]?.type === SLATE_TEXT_FORMATS.DD_VARIATION) {
    // if paste target is a DD variation, paste is as a plain text
    const text = slateToText(nodes);
    editor.insertText(text);
    return;
  }

  if (!nodes.some((node) => node.type === SLATE_TEXT_FORMATS.DD_VARIATION)) {
    // if pasted nodes doesn't have a DD variation, just insert them in the selected cursor
    Transforms.insertNodes(editor, nodes);
    return;
  }

  // we should calculate the score again for a variation node that got pasted
  // cause even if its a part of a variation, the score of the original variation got pasted as well
  const newNodes = nodes.map((node) =>
    node.type === SLATE_TEXT_FORMATS.DD_VARIATION
      ? { ...node, variation: { ...node.variation, shouldBeScored: true } }
      : node
  );

  const path = editor.selection && editor.selection.anchor.path.slice(0, -1);
  Transforms.insertNodes(editor, newNodes, { at: path });
};

export const getSelectionEdgePoints = (editor) => {
  return Editor.edges(editor, editor.selection);
};

export const getPlainTextSelection = (editor) => {
  if (!editor.selection) return '';

  const fragment = Editor.fragment(editor, editor.selection);
  return slateToText(fragment);
};

export const getSelectionNodes = (editor) => {
  if (!editor.selection) return [];

  return Editor.fragment(editor, editor.selection);
};

export const isEntireVariationSelected = (editor) => {
  const context = getPlainTextSelectionContext(editor);
  const selection = getPlainTextSelection(editor);
  return context === selection;
};

export const getPlainTextSelectionContext = (editor) => {
  if (!editor.selection) return '';

  const [start, end] = getSelectionEdgePoints(editor);

  const fragment = Editor.fragment(editor, {
    anchor: Editor.point(editor, [start.path[0]], {
      edge: 'start',
    }),
    focus: Editor.point(editor, [end.path[0]], { edge: 'end' }),
  });
  return slateToText(fragment);
};

/**
 *
 * Completes the selection to the whole context of the selected text.
 */
export const selectCompleteContext = (editor) => {
  const [start, end] = getSelectionEdgePoints(editor);

  Transforms.select(editor, {
    anchor: Editor.point(editor, [start.path[0]], {
      edge: 'start',
    }),
    focus: Editor.point(editor, [end.path[0]], { edge: 'end' }),
  });
};

export const selectCompleteEditor = (editor) => {
  ReactEditor.focus(editor);
  Transforms.select(editor, []);
};

export const selectFocusedLeaf = (editor) => {
  const path = editor.selection?.anchor?.path;
  if (!path) return;

  const pathClone = [...path];

  pathClone.pop();
  const focusedNode = pathClone?.reduce((node, pathPosition) => {
    if (!node) return editor.children[pathPosition];

    return node.children[pathPosition];
  }, null);

  Transforms.select(editor, {
    anchor: { path: editor.selection.anchor.path, offset: 0 },
    focus: { path: editor.selection.focus.path, offset: focusedNode.children[0].text.length },
  });
};

export const hasSlateContentChangeOperation = (operations) =>
  operations.some((op) => 'set_selection' !== op.type);

const cleanupHtmlContent = (htmlContent) => {
  return slateToHtml(htmlToSlate(htmlContent)).trim();
};

export const pasteFromClipboard = (editor, event) => {
  event.preventDefault();

  const htmlContent = event.clipboardData.getData('text/html');

  if (htmlContent) {
    insertNodesToEditorAtSelection(editor, htmlToSlate(cleanupHtmlContent(htmlContent)));
  } else {
    const textContent = event.clipboardData.getData('text/plain');
    editor.insertText(textContent);
  }
};

export const selectWholeNode = (editor, node) => {
  const path = ReactEditor.findPath(editor, node);
  try {
    if (Editor.hasPath(editor, path)) {
      // Clear existing selections
      Transforms.deselect(editor);

      // Select the new range
      Transforms.select(editor, Editor.range(editor, path));

      // Focus the editor
      ReactEditor.focus(editor);
    }
  } catch (e) {
    //nothing
  }
};

export const overrideContent = (editor, htmlString) => {
  Transforms.deselect(editor);

  // Remove existing nodes
  const children = [...editor.children];
  children.forEach((node) => editor.apply({ type: 'remove_node', path: [0], node }));

  // Add new nodes
  const nodes = htmlToSlate(htmlString);
  nodes.forEach((node, i) => editor.apply({ type: 'insert_node', path: [i], node: node }));
};

export const removeSlashBeforeCaret = (editor) => {
  const { selection } = editor;

  const { character, range } = getCharacterBeforeCursor(editor);
  if (selection && character === '/' && Range.isCollapsed(selection)) {
    Transforms.delete(editor, { at: range });
  }
};

export const getCharacterBeforeCursor = (editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const beforePoint = Editor.before(editor, selection, { unit: 'character' });

    if (beforePoint) {
      const range = { anchor: beforePoint, focus: selection.anchor };
      const [character] = Editor.string(editor, range);

      return { character, range };
    }
  }

  return { character: null, range: null };
};

export const getCharacterAfterCursor = (editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const afterPoint = Editor.after(editor, selection, { unit: 'character' });

    if (afterPoint) {
      const range = { anchor: afterPoint, focus: selection.anchor };
      const [character] = Editor.string(editor, range);

      return { character, range };
    }
  }

  return { character: null, range: null };
};

export const unwrapCurrentNode = (editor, node, path) => {
  if (Node.isNode(node) && !Editor.isEditor(node)) {
    Transforms.unwrapNodes(editor, { at: path });
  }
};

export const findAncestorByType = (editor, type) => {
  const { selection } = editor;

  if (!selection) {
    return null;
  }

  // Getting the path to the node where the cursor is
  const [node, path] = Editor.node(editor, selection);

  // If we couldn't find a node, return null
  if (!path) {
    return null;
  }

  // First, go over ancestors
  for (const ancestorPath of Node.ancestors(editor, path, { reverse: true })) {
    const [node] = ancestorPath;

    if (node.type === type) {
      return { node, path: ancestorPath[1] };
    }
  }

  // No ancestors of this type, check if current node is the type return it
  if (node.type === type) {
    return path;
  }

  return { node: null, path: null };
};
